import './index.less';
import slaService from '../../../../services/slaService';
import { useRequest } from 'ahooks';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useMemo, useState } from 'react';
import { Select } from 'antd';
import RevxFlexLayout from '../../../../components/RevxFlexLayout';
import { useOrgId } from '../../../../hooks/useOrg';
import { UnderlineText } from '../../../../components/RevxTypography';
import SLASku from './components/SLASku';

const { getSupplierOrgId, getSkuOptions, createSla, getSlaListByOrgId } =
  slaService;

const SLAAdminContainer = () => {
  const [showSelectDropdown, setShowSelectDropdown] = useState(false);
  const [currentOrgId, setCurrentOrgId] = useState<number>(0);

  const dispatch = useAppDispatch();
  const slaList = useAppSelector((state) => state.sla.slaList);
  const orgId = useOrgId();
  const orgList = useAppSelector((state) => state.org.list);
  const { data: supplierOrgIdList } = useRequest(getSupplierOrgId);
  const { data: skuOptions } = useRequest(getSkuOptions);
  const { runAsync: runCreateSla } = useRequest(createSla, {
    manual: true,
  });
  const { run: runGetSlaListByOrgId } = useRequest(getSlaListByOrgId, {
    manual: true,
    onSuccess: (data) => {
      dispatch.sla.setState({
        slaList: data,
      });
    },
  });

  const orgOptions = useMemo(() => {
    return orgList.map((org) => {
      const { id, name } = org;
      const hasSla = supplierOrgIdList?.includes(id);
      return {
        label: hasSla ? `${name} - SLA` : name,
        value: id,
      };
    });
  }, [orgList, supplierOrgIdList]);

  const skuSelectOptions = useMemo(() => {
    return skuOptions
      ?.filter((sku) => {
        return !slaList?.find((sla) => sla.skuId === sku.id);
      })
      .map((sku) => {
        const { id, name } = sku;
        return {
          label: name,
          value: id,
        };
      });
  }, [skuOptions, slaList]);

  const createSlaHandler = async (skuId: number) => {
    await runCreateSla({
      supplierOrgId: currentOrgId,
      skuId,
      orgId,
    });
    await runGetSlaListByOrgId(currentOrgId);
    setShowSelectDropdown(false);
  };

  return (
    <div className="sla-admin">
      <div className="title">Select an organization to define its SLA.</div>
      <RevxFlexLayout className="org-select">
        <div className="mr-10">Choose organization:</div>
        <Select
          options={orgOptions}
          showSearch={true}
          optionFilterProp="label"
          placeholder="Please select an org"
          onChange={(v: number) => {
            setCurrentOrgId(v);
            runGetSlaListByOrgId(v);
            setShowSelectDropdown(false);
          }}
        />
        <div className="ml-10">
          <span>There are </span>
          <span>{supplierOrgIdList?.length || 0}</span>
          <span> organization with SLAs today.</span>
        </div>
      </RevxFlexLayout>
      {!!currentOrgId && (
        <div className="sla-main">
          <div className="sku-title">SKU</div>
          <div className="sku-container">
            {slaList?.map((sla) => {
              return <SLASku key={sla.id} data={sla} />;
            })}
          </div>
          <div className="sku-select-container mt-20">
            {showSelectDropdown && (
              <Select
                options={skuSelectOptions}
                showSearch={true}
                optionFilterProp="label"
                placeholder="Please select a SKU"
                onChange={createSlaHandler}
              />
            )}
          </div>
          <div className="mt-20 mb-20">
            <UnderlineText
              color="blue"
              onClick={() => {
                setShowSelectDropdown(true);
              }}
            >
              + Add SKU
            </UnderlineText>
          </div>
        </div>
      )}
    </div>
  );
};

export default SLAAdminContainer;
